import { Component } from '@theme/component';
import { fetchConfig, preloadImage, onAnimationEnd, yieldToMainThread } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, CartUpdateEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

// RC PATCH (D-048): how long a queued Add to Cart waits for the variant update before it is added anyway
const QUEUE_FAILSAFE_MS = 8000;

// RC PATCH (D-048): longest an in-flight add blocks further clicks. Time-bounded rather than aborted, because the
// server may already have applied a stalled request. Must exceed cart-conflict-retry's 409 backoff (~2 s).
const ADD_LOCK_MAX_MS = 15000;

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number[] | undefined} */
  #resetTimeouts = /** @type {number[]} */ ([]);

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#resetTimeouts) {
      this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    }
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    // Check if adding would exceed max before animating
    const productForm = /** @type {ProductFormComponent | null} */ (this.closest('product-form-component'));
    const quantitySelector = productForm?.refs.quantitySelector;
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      // Don't animate if it would exceed max
      if (!validation.canAdd) {
        return;
      }
    }
    // RC PATCH (D-048): skip the "added" / fly-to-cart animation when ProductFormComponent will ignore this click
    // or cannot yet know that it adds anything (QA DEF-001 / DEF-017).
    if (productForm?.shouldSkipAddToCartAnimation) return;

    if (this.refs.addToCartButton.dataset.puppet !== 'true') {
      const animationEnabled = this.dataset.addToCartAnimation === 'true';
      if (animationEnabled && !event.target.closest('.quick-add-modal')) {
        this.#animateFlyToCart();
      }
      this.animateAddToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    let flyToCartClass = addToCartButton.classList.contains('quick-add__button')
      ? 'fly-to-cart--quick'
      : 'fly-to-cart--main';

    flyToCartElement.classList.add(flyToCartClass);
    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.style.setProperty('--start-opacity', '0');
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart = async function () {
    const { addToCartButton } = this.refs;

    // Initialize the array if it doesn't exist
    if (!this.#resetTimeouts) {
      this.#resetTimeouts = [];
    }

    // Clear all existing timeouts
    this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    this.#resetTimeouts = [];

    if (addToCartButton.dataset.added !== 'true') {
      addToCartButton.dataset.added = 'true';
    }

    // The onAnimationEnd can trigger a style recalculation so we yield to the main thread first.
    await yieldToMainThread();
    await onAnimationEnd(addToCartButton);

    // Create new timeout and store it in the array
    const timeoutId = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');

      // Remove this timeout from the array
      const index = this.#resetTimeouts.indexOf(timeoutId);
      if (index > -1) {
        this.#resetTimeouts.splice(index, 1); 
      }
    }, 800);

    this.#resetTimeouts.push(timeoutId);
  };
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * RC PATCH (D-048): an Add to Cart pressed while a variant change was loading.
 * `generation` is the selection it was pressed under; `variantId` is the hint captured at click time ('' = none);
 * `hintExact` says whether that hint can be trusted without the server's resolution (single-option pickers only).
 * @typedef {{variantId: string, quantity: number, generation: number, hintExact: boolean, properties?: Record<string, string>, selling_plan?: string}} QueuedAddToCartItem
 */

/**
 * A custom element that manages a product form.
 *
 * @typedef {{items: Array<{quantity: number, variant_id: number}>}} Cart
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 * @property {HTMLElement | undefined} volumePricing - The volume pricing component.
 * @property {any | undefined} quantitySelector - The quantity selector component.
 * @property {HTMLElement | undefined} quantitySelectorWrapper - The quantity selector wrapper element.
 * @property {HTMLElement | undefined} quantityLabel - The quantity label element.
 * @property {HTMLElement | undefined} pricePerItem - The price per item component.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /** @type {boolean} */
  #variantChangeInProgress = false;

  /** @type {QueuedAddToCartItem[]} RC PATCH (D-048): item shape extended, see the typedef above */
  #addToCartQueue = [];

  // RC PATCH (D-048) — QA DEF-017 (wrong variant added during a variant load) and DEF-001 (double add).
  /**
   * Hint for the variant the latest option choice resolves to, from the chosen option's data-variant-id:
   * undefined = unknown, '' = no such variant. Exact for single-option products; for multi-option products it can be
   * stale when a second option changes mid-load, so queued clicks of the latest selection are re-resolved from the
   * variant update (see #resolveQueuedItems). @type {string | undefined}
   */
  #pendingVariantId = undefined;

  /** Availability hint for that choice (false = sold out); same staleness caveat. @type {boolean} */
  #pendingVariantAvailable = true;

  /**
   * Whether that hint is exact: true only for a single-option picker. With two or more options, Liquid resolves
   * data-variant-id against the other options as rendered (stale after an unfinished change) and
   * product_option_value.available only top-down, so such hints are never trusted without the server. @type {boolean}
   */
  #pendingHintExact = true;

  /** Product this form showed when the pending selection was made. @type {string | undefined} */
  #pendingProductId = undefined;

  /** Incremented per accepted selection; queued clicks remember theirs. @type {number} */
  #selectionGeneration = 0;

  /** /cart/add requests from this form still in flight, each with its start time. @type {Set<{startedAt: number}>} */
  #addRequests = new Set();

  /** Timer that adds queued items if the variant update never arrives. @type {number | undefined} */
  #queueFailsafe;

  /**
   * RC PATCH (D-048): whether the "added" / fly-to-cart animation should be skipped for the next click. Mirrors
   * handleSubmit: true when the click will be ignored (an add in flight on a settled page, or this selection already
   * queued) or when it cannot yet be known to add anything (an exact hint saying "no purchasable variant", or any
   * multi-option hint while the variant loads — the cart drawer opening is the feedback if it does add).
   * Read by AddToCartComponent and the sticky add-to-cart bar.
   * @returns {boolean}
   */
  get shouldSkipAddToCartAnimation() {
    if (this.#pendingChangeIsStale()) return this.#addLockActive(); // handleSubmit resets, then adds directly unless locked
    if (this.#variantChangeInProgress) {
      const alreadyQueued = this.#addToCartQueue.some((item) => item.generation === this.#selectionGeneration);
      return alreadyQueued || !this.#pendingHintExact || !this.#getIntendedVariantId();
    }
    return this.#addLockActive();
  }

  /**
   * RC PATCH (D-048): an add request younger than ADD_LOCK_MAX_MS is in flight. Per-request start times, so a
   * stalled request stops blocking after the bound even while later adds come and go.
   * @returns {boolean}
   */
  #addLockActive() {
    const now = Date.now();
    for (const request of this.#addRequests) {
      if (now - request.startedAt < ADD_LOCK_MAX_MS) return true;
    }
    return false;
  }

  /** RC PATCH (D-048) @returns {{startedAt: number}} */
  #beginAddRequest() {
    const request = { startedAt: Date.now() };
    this.#addRequests.add(request);
    return request;
  }

  /** RC PATCH (D-048) @param {{startedAt: number}} request */
  #endAddRequest(request) {
    this.#addRequests.delete(request);
  }

  /**
   * RC PATCH (D-048): fetch, with a synchronous throw from a fetch wrapper (apps patch window.fetch) turned into a
   * rejection, so the caller's .finally still runs and releases the add lock.
   * @param {string} url
   * @param {RequestInit} init
   * @returns {Promise<Response>}
   */
  #fetchSafely(url, init) {
    try {
      return Promise.resolve(fetch(url, init)); // also tolerates a wrapper that returns a non-promise
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * RC PATCH (D-048): the form now shows a different product than when the pending selection was made (e.g. a
   * quick-add form reused by morph after a load that never finished). That state must not leak into this product.
   * @returns {boolean}
   */
  #pendingChangeIsStale() {
    return (
      this.#variantChangeInProgress &&
      this.#pendingProductId !== undefined &&
      this.#pendingProductId !== this.dataset.productId
    );
  }

  /** RC PATCH (D-048): drop the pending selection, its queued clicks and the failsafe. */
  #resetPendingChange() {
    this.#variantChangeInProgress = false;
    this.#pendingVariantId = undefined;
    this.#pendingVariantAvailable = true;
    this.#pendingHintExact = true;
    this.#pendingProductId = undefined;
    this.#addToCartQueue = [];
    this.#clearQueueFailsafe();
  }

  /**
   * RC PATCH (D-056, D-048c I-A): quick-add reuses this form across dialog opens. Drop a click still held and the
   * pending variant state from the previous open (never add it), so a reopen starts clean.
   */
  resetPendingVariantChange() {
    this.#resetPendingChange();
  }

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.#clearQueueFailsafe(); // RC PATCH (D-048)
  }

  /**
   * Updates quantity selector with cart data for current variant
   * @param {Cart} cart - The cart object with items array
   * @returns {number} The cart quantity for the current variant
   */
  #updateCartQuantityFromData(cart) {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value || !cart?.items) return 0;

    const cartItem = cart.items.find((item) => item.variant_id.toString() === variantIdInput.value.toString());
    const cartQty = cartItem ? cartItem.quantity : 0;

    // Use public API to update quantity selector
    const quantitySelector = /** @type {any | undefined} */ (this.querySelector('quantity-selector-component'));
    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(cartQty);
    }

    // Update quantity label if it exists
    this.#updateQuantityLabel(cartQty);

    return cartQty;
  }

  /**
   * Fetches the current cart and updates the quantity display.
   * @returns {Promise<Cart | null>} The cart object, or null if fetch fails.
   */
  async #fetchAndUpdateCartQuantity() {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value) return null;

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      this.#updateCartQuantityFromData(cart);
      return cart;
    } catch (error) {
      console.error('Failed to fetch cart quantity:', error);
      return null;
    }
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartUpdateEvent|CartAddEvent} event
   */
  #onCartUpdate = async (event) => {
    // Skip if this event came from this component
    if (event.detail?.sourceId === this.id || event.detail?.data?.source === 'product-form-component') return;

    const cart = /** @type {Cart} */ (event.detail?.resource);
    if (cart?.items) {
      this.#updateCartQuantityFromData(cart);
    } else {
      await this.#fetchAndUpdateCartQuantity();
    }
  };

  /** @param {Event} event */
  handleSubmit(event) {
    event.preventDefault();

    // RC PATCH (D-048): state left over from another product must not leak into this one; drop it and add directly.
    if (this.#pendingChangeIsStale()) this.#resetPendingChange();

    if (this.#variantChangeInProgress) {
      const generation = this.#selectionGeneration;

      // RC PATCH (D-048): one queued click per selection, so a double click while the variant loads adds once
      // (DEF-001). The click is queued even when the hint says "no variant" or "sold out": the variant update
      // resolves the real variant for this selection and drops the click if it isn't purchasable (DEF-017).
      if (this.#addToCartQueue.some((item) => item.generation === generation)) return;

      const hintVariantId = this.#getIntendedVariantId();
      const quantity = this.#getQuantity();

      this.#addToCartQueue.push({
        variantId: hintVariantId ?? '',
        quantity,
        generation,
        hintExact: this.#pendingHintExact,
        ...this.#getLineItemFields(),
      });
      this.#armQueueFailsafe();
      // RC PATCH (D-048): animate only when an exact hint says this click adds a purchasable variant
      if (this.#pendingHintExact && hintVariantId) this.refs.addToCartButtonContainer?.animateAddToCart?.();
      return;
    }

    this.#processAddToCart(undefined, undefined, event);
  }

  /** @returns {string | undefined} */
  #getIntendedVariantId() {
    // RC PATCH (D-048): prefer the hint captured when the option was chosen (QA DEF-017); undefined when it says the
    // choice has no purchasable variant. Horizon 3.5.1 read ?variant= from the URL, written a frame later (never in
    // hidden tabs, never in quick-add dialogs), else the form's id, i.e. the previous variant. That fallback is kept
    // only for VariantSelectedEvents without variantId (none are dispatched in this build).
    if (this.#pendingVariantId !== undefined) {
      return this.#pendingVariantAvailable && this.#pendingVariantId ? this.#pendingVariantId : undefined;
    }
    return new URL(window.location.href).searchParams.get('variant') || this.refs.variantId?.value || undefined;
  }

  /**
   * RC PATCH (D-048): the form's line-item fields at click time, so a queued add creates the same cart line a
   * direct add would. Horizon 3.5.1 queued only {id, quantity}, which dropped properties[...] (e.g. Intelligems
   * _igTestGroup attribution) and selling_plan (a subscription would be added as a one-time purchase).
   * @returns {{properties?: Record<string, string>, selling_plan?: string}}
   */
  #getLineItemFields() {
    const form = this.querySelector('form');
    if (!form) return {};

    const data = new FormData(form);
    /** @type {Record<string, string>} */
    const properties = {};
    for (const [key, value] of data.entries()) {
      const match = /^properties\[(.+)\]$/.exec(key);
      if (match?.[1] && typeof value === 'string') properties[match[1]] = value;
    }
    const sellingPlan = data.get('selling_plan');

    return {
      ...(Object.keys(properties).length > 0 ? { properties } : {}),
      ...(typeof sellingPlan === 'string' && sellingPlan !== '' ? { selling_plan: sellingPlan } : {}),
    };
  }

  /**
   * RC PATCH (D-048): Horizon 3.5.1 drains the queue only on variant:update. If the section fetch fails (network
   * error, or a response without the picker JSON) that event never comes and every later click is swallowed.
   * After QUEUE_FAILSAFE_MS, point the form at the chosen variant, add the queued items, and unblock the form.
   */
  #armQueueFailsafe() {
    if (this.#queueFailsafe) return;

    this.#queueFailsafe = setTimeout(() => {
      this.#queueFailsafe = undefined;
      if (!this.#variantChangeInProgress || this.#addToCartQueue.length === 0) return;

      // The form was re-pointed at another product since the click: never add the old product's choice.
      if (this.#pendingChangeIsStale()) {
        this.#resetPendingChange();
        return;
      }

      // The form cannot say which product it shows (no data-product-id). Like pristine Horizon it ignores every
      // update, so it stays loading (later clicks keep queueing) and its queued clicks are dropped: never add a guess.
      if (!this.dataset.productId) {
        this.#addToCartQueue = [];
        return;
      }

      // An inexact (multi-option) latest hint can't be trusted without the server. Keep the queue and the loading
      // state so a late update still resolves these clicks exactly; disable the button meanwhile (that update, or
      // the next selection's update, re-enables it).
      if (!this.#pendingHintExact) {
        console.warn('[product-form] variant update is late; holding the queued clicks for it');
        this.refs.addToCartButtonContainer?.disable();
        return;
      }

      // An exact hint names the chosen variant, purchasable or not. Point the form at it, so a later direct click
      // posts that variant (a sold-out one gets Shopify's "sold out" error) and never the previous one (QA DEF-017).
      if (this.#pendingVariantId !== undefined) this.refs.variantId.value = this.#pendingVariantId;
      const queuedItems = this.#addToCartQueue.filter((item) => item.hintExact && item.variantId);
      console.warn(`[product-form] variant update did not arrive; adding ${queuedItems.length} queued item(s)`);
      this.#resetPendingChange();
      this.#processBatchAddToCart(queuedItems);
    }, QUEUE_FAILSAFE_MS);
  }

  /**
   * RC PATCH (D-048): the variant each queued click should add. Clicks of the latest selection take the variant the
   * update resolved (the picker aborts older fetches, so the update that arrives is for the latest selection); a
   * null or unavailable variant drops them, and the button disables. Clicks of a superseded selection keep the id
   * captured at click time only when that hint was exact (single-option); a superseded multi-option hint may name a
   * combination the shopper never saw, so such clicks are dropped.
   * @param {QueuedAddToCartItem[]} items
   * @param {{id?: number | string, available?: boolean} | null | undefined} resolvedVariant
   * @returns {QueuedAddToCartItem[]}
   */
  #resolveQueuedItems(items, resolvedVariant) {
    const latest = this.#selectionGeneration;
    /** @type {QueuedAddToCartItem[]} */
    const resolved = [];

    for (const item of items) {
      if (item.generation === latest) {
        if (resolvedVariant?.id && resolvedVariant.available !== false) {
          resolved.push({ ...item, variantId: String(resolvedVariant.id) });
        }
      } else if (item.hintExact && item.variantId) {
        resolved.push(item);
      }
    }

    return resolved;
  }

  /** RC PATCH (D-048) */
  #clearQueueFailsafe() {
    if (this.#queueFailsafe) clearTimeout(this.#queueFailsafe);
    this.#queueFailsafe = undefined;
  }

  /** @returns {number} */
  #getQuantity() {
    return Number(this.refs.quantitySelector?.getValue?.()) || Number(this.dataset.quantityDefault) || 1;
  }

  /**
   * @param {string} [overrideVariantId]
   * @param {number} [overrideQuantity]
   * @param {Event} [event]
   */
  #processAddToCart(overrideVariantId, overrideQuantity, event) {
    const { addToCartTextError } = this.refs;

    // RC PATCH (D-048): ignore a second submit while an add from this form is still in flight (QA DEF-001),
    // for at most ADD_LOCK_MAX_MS so a stalled request can't block the form indefinitely.
    if (this.#addLockActive()) return;

    if (this.#timeout) clearTimeout(this.#timeout);

    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );

    if (!overrideVariantId) {
      const anyButtonDisabled = Array.from(allAddToCartContainers).some(
        (container) => container.refs.addToCartButton?.disabled
      );
      if (anyButtonDisabled) return;
    }

    const form = this.querySelector('form');
    if (!form) throw new Error('Product form element missing');

    if (!overrideVariantId && this.refs.quantitySelector?.canAddToCart) {
      const validation = this.refs.quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        for (const container of allAddToCartContainers) {
          container.disable();
        }

        const errorTemplate = this.dataset.quantityErrorMax || '';
        const errorMessage = errorTemplate.replace('{{ maximum }}', validation.maxQuantity?.toString() || '');
        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');

          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = errorMessage;
          } else {
            const newTextNode = document.createTextNode(errorMessage);
            addToCartTextError.appendChild(newTextNode);
          }

          this.#setLiveRegionText(errorMessage);

          if (this.#timeout) clearTimeout(this.#timeout);
          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }

        setTimeout(() => {
          for (const container of allAddToCartContainers) {
            container.enable();
          }
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    const formData = new FormData(form);

    if (overrideVariantId) {
      formData.set('id', overrideVariantId);
    }
    if (overrideQuantity !== undefined) {
      formData.set('quantity', overrideQuantity.toString());
    }

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    });

    const fetchCfg = fetchConfig('javascript', { body: formData });

    const addRequest = this.#beginAddRequest(); // RC PATCH (D-048): released in .finally below
    let responded = false; // RC PATCH (D-054): set once a JSON response arrives
    this.#fetchSafely(Theme.routes.cart_add_url, { // RC PATCH (D-048): was fetch(...)
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => {
        // RC PATCH (D-054): a fetch wrapper that swallows a network failure can resolve without a Response;
        // that is a failed add (the generic error), not a TypeError further down.
        if (!response || typeof response.json !== 'function') throw new Error('Add to cart: no response');
        // RC PATCH (D-054): production's bot-challenge handling, see #isChallengeResponse
        if (this.#isChallengeResponse(response)) {
          responded = true;
          this.#handleChallenge(form.getAttribute('id') || '');
        }
        return response.json();
      })
      .then(async (response) => {
        responded = true; // RC PATCH (D-054)
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
          );

          // RC PATCH (D-071): stock Horizon returned here when the form had no error line (the card's Add button),
          // so the error was neither shown nor announced and the failed add was never reported (QA DEF-042). The
          // line is optional now.
          if (this.#isRecipientFieldError(response)) {
            this.#leaveToRecipientForm(addToCartTextError);
            return;
          }

          if (addToCartTextError) {
            addToCartTextError.classList.remove('hidden');

            // Reuse the text node if the user is spam-clicking
            const textNode = addToCartTextError.childNodes[2];
            if (textNode) {
              textNode.textContent = response.message;
            } else {
              const newTextNode = document.createTextNode(response.message);
              addToCartTextError.appendChild(newTextNode);
            }
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          if (this.#timeout) clearTimeout(this.#timeout);
          this.#timeout = setTimeout(() => {
            if (addToCartTextError) addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          // Get the added text from any add-to-cart button
          const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
          if (anyAddToCartButton) {
            const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, SUCCESS_MESSAGE_DISPLAY_DURATION);
          }

          // Fetch the updated cart to get the actual total quantity for this variant
          const cart = await this.#fetchAndUpdateCartQuantity();

          this.dispatchEvent(
            new CartAddEvent(cart ?? undefined, id.toString(), {
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );
        }
      })
      .catch((error) => {
        console.error(error);
        if (!responded) this.#showAddToCartError(form.getAttribute('id') || ''); // RC PATCH (D-054)
      })
      .finally(() => {
        this.#endAddRequest(addRequest); // RC PATCH (D-048)
        if (event) {
          cartPerformance.measureFromEvent('add:user-action', event);
        }
      });
  }

  /** @param {QueuedAddToCartItem[]} items RC PATCH (D-048): item shape extended */
  #processBatchAddToCart(items) {
    if (items.length === 0) return;

    const { addToCartTextError } = this.refs;

    if (this.#timeout) clearTimeout(this.#timeout);

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const cartItemComponentsSectionIds = [];
    for (const item of cartItemsComponents) {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    }

    const payload = {
      items: items.map((item) => ({
        id: Number(item.variantId),
        quantity: item.quantity,
        // RC PATCH (D-048): same line-item fields a direct add submits (see #getLineItemFields)
        ...(item.properties ? { properties: item.properties } : {}),
        ...(item.selling_plan ? { selling_plan: Number(item.selling_plan) } : {}),
      })),
      sections: cartItemComponentsSectionIds.join(','),
    };

    const addRequest = this.#beginAddRequest(); // RC PATCH (D-048): released in .finally below
    let responded = false; // RC PATCH (D-054): set once a JSON response arrives
    this.#fetchSafely(Theme.routes.cart_add_url, { // RC PATCH (D-048): was fetch(...)
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    })
      .then((response) => {
        // RC PATCH (D-054): a fetch wrapper that swallows a network failure can resolve without a Response;
        // that is a failed add (the generic error), not a TypeError further down.
        if (!response || typeof response.json !== 'function') throw new Error('Add to cart: no response');
        // RC PATCH (D-054): production's bot-challenge handling, see #isChallengeResponse
        if (this.#isChallengeResponse(response)) {
          responded = true;
          this.#handleChallenge(this.id);
        }
        return response.json();
      })
      .then(async (response) => {
        responded = true; // RC PATCH (D-054)
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(this.id, response.message, response.description, response.errors)
          );

          // RC PATCH (D-071): as the direct add above: recipient-field errors go to the recipient form, and the
          // error is announced whether or not the form has an error line.
          if (this.#isRecipientFieldError(response)) {
            this.#leaveToRecipientForm(addToCartTextError);
            return;
          }

          if (addToCartTextError) {
            addToCartTextError.classList.remove('hidden');
            const textNode = addToCartTextError.childNodes[2];
            if (textNode) {
              textNode.textContent = response.message;
            } else {
              addToCartTextError.appendChild(document.createTextNode(response.message));
            }
          }
          this.#setLiveRegionText(response.message);

          if (this.#timeout) clearTimeout(this.#timeout);
          this.#timeout = setTimeout(() => {
            addToCartTextError?.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);

          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
              productId: this.dataset.productId,
            })
          );
          return;
        }

        if (addToCartTextError) {
          addToCartTextError.classList.add('hidden');
          addToCartTextError.removeAttribute('aria-live');
        }

        const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
          this.querySelectorAll('add-to-cart-component')
        );
        const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
        if (anyAddToCartButton) {
          const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
          const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;
          this.#setLiveRegionText(addedText);
          setTimeout(() => this.#clearLiveRegionText(), SUCCESS_MESSAGE_DISPLAY_DURATION);
        }

        const cart = await this.#fetchAndUpdateCartQuantity();

        const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
        this.dispatchEvent(
          new CartAddEvent(cart ?? undefined, this.id, {
            source: 'product-form-component',
            itemCount: totalQuantity,
            productId: this.dataset.productId,
            sections: response.sections,
          })
        );
      })
      .catch((error) => {
        console.error(error);
        if (!responded) this.#showAddToCartError(this.id); // RC PATCH (D-054)
      })
      .finally(() => {
        this.#endAddRequest(addRequest); // RC PATCH (D-048)
      });
  }

  /**
   * RC PATCH (D-071): whether a 422 is about a gift card's recipient fields. The recipient form shows those beside
   * its fields and announces them; it reads them from `errors`, or from `description` when `errors` is not an
   * object (gift-card-recipient-form.js #displayCartError).
   * @param {{ errors?: unknown, description?: unknown }} response
   * @returns {boolean}
   */
  #isRecipientFieldError(response) {
    if (!this.querySelector('gift-card-recipient-form')) return false;
    const source = response.errors && typeof response.errors === 'object' ? response.errors : response.description;
    if (!source || typeof source !== 'object') return false;
    return Object.keys(source).some((key) => ['email', 'name', 'message', 'send_on'].includes(key));
  }

  /**
   * RC PATCH (D-071): leave a recipient-field 422 to the recipient form. Hide this form's line and announcement (no
   * earlier error's message beside the field errors) and send no failed-add cart:update: the recipient form clears
   * its field errors on any cart:update, and nothing was added.
   * @param {HTMLElement | undefined} addToCartTextError
   */
  #leaveToRecipientForm(addToCartTextError) {
    if (this.#timeout) clearTimeout(this.#timeout);
    addToCartTextError?.classList.add('hidden');
    this.#clearLiveRegionText();
  }

  /**
   * RC PATCH (D-054): an add with no JSON response (a network failure, or a body that isn't JSON) was only
   * logged, so the shopper saw the success animation and nothing else (QA DEF-020). Show and announce a
   * generic error the way a 422 is shown. Display only, never a retry: a lost response may still have
   * added the item. The CartErrorEvent also resets the sticky bar's pending click.
   * @param {string} sourceId
   * @param {string} [message]
   */
  #showAddToCartError(sourceId, message = Theme.translations.add_to_cart_error || 'An error occurred. Please try again.') {
    try {
      this.dispatchEvent(new CartErrorEvent(sourceId, message, '', {}));

      const { addToCartTextError } = this.refs;
      if (addToCartTextError) {
        addToCartTextError.classList.remove('hidden');
        const textNode = addToCartTextError.childNodes[2];
        if (textNode) {
          textNode.textContent = message;
        } else {
          addToCartTextError.appendChild(document.createTextNode(message));
        }
      }
      // RC PATCH (D-071): a gift card's recipient form announces every cart error itself, and no cart:update
      // follows this one to clear it, so announcing here too would read it twice.
      if (!this.querySelector('gift-card-recipient-form')) this.#setLiveRegionText(message);

      if (this.#timeout) clearTimeout(this.#timeout);
      this.#timeout = setTimeout(() => {
        addToCartTextError?.classList.add('hidden');
        this.#clearLiveRegionText();
      }, ERROR_MESSAGE_DISPLAY_DURATION);
    } catch (error) {
      console.error(error);
    }
  }

  /**
   * RC PATCH (D-054): production's buy box treats an HTML, 403 or 429 answer to an add as a bot challenge
   * (current/assets/theme.js addError): retrying would only be challenged again.
   * @param {Response} response
   * @returns {boolean}
   */
  #isChallengeResponse(response) {
    const contentType = response.headers.get('content-type') || '';
    return contentType.includes('text/html') || response.status === 403 || response.status === 429;
  }

  /**
   * RC PATCH (D-054): as production, say so and reload the page for the verification. The thrown error ends
   * the add chain: its .catch only logs (responded is set) and .finally releases the add lock.
   * @param {string} sourceId
   */
  #handleChallenge(sourceId) {
    console.error('[Cart] Bot challenge on cart/add (HTML, 403 or 429 instead of JSON). Reloading page for verification.');
    // Production's own text, English only as on production.
    this.#showAddToCartError(sourceId, 'Please verify your connection. Reloading page…');
    setTimeout(() => window.location.reload(), 2000);
    throw new Error('CF_BLOCK');
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement('beforebegin', /** @type {Element} */ (newElement.cloneNode(true)));
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = async (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId } = this.refs;
    variantId.value = event.detail.resource?.id ?? '';

    // RC PATCH (D-048): resolve the queued clicks against this update, then drop the pending selection, the queue
    // and the failsafe (replaces Horizon's `#variantChangeInProgress = false` + drain of the raw queue).
    const queuedItems = this.#resolveQueuedItems(this.#addToCartQueue, event.detail.resource);
    this.#resetPendingChange();
    this.#processBatchAddToCart(queuedItems);

    const { addToCartButtonContainer: currentAddToCartButtonContainer, acceleratedCheckoutButtonContainer } = this.refs;
    const currentAddToCartButton = currentAddToCartButtonContainer?.refs.addToCartButton;

    // Update state and text for add-to-cart button
    if (!currentAddToCartButtonContainer || (!currentAddToCartButton && !acceleratedCheckoutButtonContainer)) return;

    // Update the button state
    if (event.detail.resource == null || event.detail.resource.available == false) {
      currentAddToCartButtonContainer.disable();
    } else {
      currentAddToCartButtonContainer.enable();
    }

    const newAddToCartButton = event.detail.data.html.querySelector('product-form-component [ref="addToCartButton"]');
    if (newAddToCartButton && currentAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    if (acceleratedCheckoutButtonContainer) {
      if (event.detail.resource == null || event.detail.resource.available == false) {
        acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      } else {
        acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
      }
    }

    // Set the data attribute for the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      if (productVariantMedia) {
        this.refs.addToCartButtonContainer?.setAttribute(
          'data-product-variant-media',
          productVariantMedia + '&width=100'
        );
      }
    }

    // Check if quantity rules, price-per-item, or add-to-cart are appearing/disappearing (causes layout shift)
    const {
      quantityRules,
      pricePerItem,
      quantitySelector,
      productFormButtons,
      quantityLabel,
      quantitySelectorWrapper,
    } = this.refs;

    // Update quantity selector's min/max/step attributes and cart quantity for the new variant
    const newQuantityInput = /** @type {HTMLInputElement | null} */ (
      event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
    );

    if (quantitySelector?.updateConstraints && newQuantityInput) {
      quantitySelector.updateConstraints(newQuantityInput.min, newQuantityInput.max || null, newQuantityInput.step);
    }

    const newQuantityRules = event.detail.data.html.querySelector('.quantity-rules');
    const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

    const newPricePerItem = event.detail.data.html.querySelector('price-per-item');
    const isPricePerItemChanging = !!pricePerItem !== !!newPricePerItem;

    if ((isQuantityRulesChanging || isPricePerItemChanging) && quantitySelector) {
      // Store quantity value before morphing entire container
      const currentQuantityValue = quantitySelector.getValue?.();

      const newProductFormButtons = event.detail.data.html.querySelector('.product-form-buttons');

      if (productFormButtons && newProductFormButtons) {
        morph(productFormButtons, newProductFormButtons);

        // Get the NEW quantity selector after morphing and update its constraints
        const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
          event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
        );

        if (this.refs.quantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
          // Temporarily set the old value so updateConstraints can snap it properly
          this.refs.quantitySelector.setValue(currentQuantityValue);
          // updateConstraints will snap to valid increment if needed
          this.refs.quantitySelector.updateConstraints(
            newQuantityInputElement.min,
            newQuantityInputElement.max || null,
            newQuantityInputElement.step
          );
        }
      }
    } else {
      // Update elements individually when layout isn't changing
      /** @type {Array<[string, HTMLElement | undefined, HTMLElement | undefined]>} */
      const morphTargets = [
        ['.quantity-label', quantityLabel, quantitySelector],
        ['.quantity-rules', quantityRules, this.refs.productFormButtons],
        ['price-per-item', pricePerItem, quantitySelectorWrapper],
      ];

      for (const [selector, currentElement, fallback] of morphTargets) {
        this.#morphOrUpdateElement(currentElement, event.detail.data.html.querySelector(selector), fallback);
      }
    }

    // Morph volume pricing if it exists
    const currentVolumePricing = this.refs.volumePricing;
    const newVolumePricing = event.detail.data.html.querySelector('volume-pricing');
    this.#morphOrUpdateElement(currentVolumePricing, newVolumePricing, this.refs.productFormButtons);

    const hasB2BFeatures =
      quantityRules || newQuantityRules || pricePerItem || newPricePerItem || currentVolumePricing || newVolumePricing;

    if (!hasB2BFeatures) return;

    // Fetch and update cart quantity for the new variant
    await this.#fetchAndUpdateCartQuantity();
  };

  /** @param {import('./events').VariantSelectedEvent} event RC PATCH (D-048): parameter now used */
  #onVariantSelected = (event) => {
    // RC PATCH (D-048): only react to this form's own product. Upstream Horizon main ignores selections from a
    // product card that doesn't contain the form; a picker for another product inside the section is ignored too.
    const picker = event?.target instanceof HTMLElement ? event.target : null;
    const sourceCard = picker?.closest('product-card');
    if (sourceCard && !sourceCard.contains(this)) return;
    const pickerProductId = picker?.dataset.productId;
    if (pickerProductId && this.dataset.productId && pickerProductId !== this.dataset.productId) return;

    // RC PATCH (D-048): a selection on a form re-pointed at another product drops that product's leftovers first.
    if (this.#pendingChangeIsStale()) this.#resetPendingChange();

    this.#variantChangeInProgress = true;
    this.#selectionGeneration += 1; // RC PATCH (D-048)
    this.#pendingProductId = this.dataset.productId; // RC PATCH (D-048)
    this.#pendingHintExact = !!picker && picker.querySelectorAll('fieldset, select').length <= 1; // RC PATCH (D-048)
    // RC PATCH (D-048): measure the failsafe from the latest selection's fetch, not the first queued click
    if (this.#queueFailsafe) {
      this.#clearQueueFailsafe();
      this.#armQueueFailsafe();
    }

    // RC PATCH (D-048): remember which variant this choice resolves to (sent by variant-picker.js).
    const resource = /** @type {{variantId?: string, variantAvailable?: boolean} | undefined} */ (
      event?.detail?.resource
    );
    if (resource && 'variantId' in resource) {
      this.#pendingVariantId = String(resource.variantId ?? '');
      this.#pendingVariantAvailable = resource.variantAvailable !== false;
    } else {
      this.#pendingVariantId = undefined;
      this.#pendingVariantAvailable = true;
    }
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}
